<template>
  <div class="mt-3 h-100 card">
    <div class="d-flex flex-between-center py-2 border-200 border-bottom card-header">
      <h6 class="mb-0">
        {{ setdevice.devicename }}
      </h6>
      <img
        class="mr-2"
        style="cursor:pointer"
        :src="setting"
        alt="설정"
        height="24"
        @click="settingDevice"
      >
    </div>
        
    <div
      class="overflow-hidden card"
      style="min-width: 12rem"
    >
      <div
        class="bg-card bg-holder"
        :style="{ backgroundImage: `url(${corner})` }"
      /><!--/.bg-holder-->
      <div class="col-xxl-12 col-lg-12">
        <div class="mb-3 p-2 row g-3">
          <div
            v-for="sensor in sensorList"
            :key="sensor.dfkey"
            class="col-sm-3"
          >
            <MonitorSensor
              :setsensor="sensor"
              :setdevice="setdevice"
              @config-updated="handleConfigUpdated"
              @device-updated="handleDeviceUpdated"
            />
          </div> 
        </div>
      </div>
    </div>
  </div>

  <ConfigSettingModal 
    v-if="ConfigShowModal" 
    :setdevice="setdevice"
    @close="ConfigShowModal = false"
  />
</template>


<script setup lang="ts">
import type { Device, DeviceData, SensorData } from '@/types/commontypes'
import MonitorSensor from '../components/MonitorSensor.vue'
import ConfigSettingModal from '../components/ConfigSettingModal.vue'
import { onMounted, watch, ref } from 'vue';
import corner from '@/assets/images/corner-1.png' 
import setting from '@/assets/images/setting.svg' 

const ConfigShowModal = ref(false)

const props = defineProps<{
    setdevice: Device;
    devicedata?: DeviceData
}>();

const emit = defineEmits<{
    'client-device-updated': [deviceid: string]
    'config-updated': [data: { deviceid: string, config: string, alertconfig: string }]
}>()

const sensorList = ref<SensorData[]>([])
const setChartDataList = ref<Record<string, unknown>>({})

onMounted(async () =>{
  initDevice();
});


watch(
  () => [props.devicedata, props.setdevice],
  ([_newDevData, _newSetDev], [_oldDevData, _oldSetDev]) => {
    LoadDeviceByChartData();
  },
  { deep: true }
);

function initDevice() {
    const svlist = props.setdevice.svlist;

    // 안전한 파싱
    let parsed: unknown = [];
    try {
        parsed = typeof svlist === 'string' ? JSON.parse(svlist) : svlist ?? [];
        if (!Array.isArray(parsed)) parsed = [];
    } catch (err) {
        console.warn('MonitorDevice: svlist 파싱 실패', err);
        parsed = [];
    }

    sensorList.value = parsed as SensorData[];

    const dataObj = props.devicedata;
    if (!dataObj) return;

    // dataObj를 문자열 키로 안전하게 인덱싱하기 위해 먼저 unknown으로 캐스트한 다음 Record로 변환
    const dataRecord = dataObj as unknown as Record<string, unknown>;

    sensorList.value = sensorList.value.map((sitem) => {
        const sitemAny = sitem;
        const sindex = sitemAny.sindex;
        const dfkey = sitemAny.dfkey ?? (sindex !== undefined ? `sv${sindex}` : undefined);

        if (!dfkey) {
            console.warn('MonitorDevice: 센서 항목에 dfkey가 없음', sitem);
        }

        const chartDataObj = setChartDataList.value;
       
        const rawChartData = chartDataObj?.[dfkey];
        const chartdata: Record<string, unknown>[] = Array.isArray(rawChartData) 
        ? rawChartData as Record<string, unknown>[]
        : [];
        
        const retjson: SensorData = {...sitem}
        const rawValue = dfkey ? dataRecord[dfkey] : undefined;
        const setcinfo = dfkey ? dataRecord[`${dfkey}info`] : undefined;
        if(setcinfo) {
          const sinfo = JSON.parse(setcinfo as string)
          retjson.strength = sinfo.strength ?? -1
          retjson.recvdt = sinfo.recvdt
        } 

        const dp = sitem.dpoint ?? 0;
        const numValue = rawValue !== undefined && rawValue !== null ? Number(rawValue) : null;
        retjson.value = numValue !== null && !isNaN(numValue)
            ? numValue.toFixed(dp)
            : (rawValue !== undefined && rawValue !== null ? String(rawValue) : '');
        retjson.chartdata = chartdata

        return retjson
    });

    // console.log('testlog MonitorDevice : ', sensorList.value)
}


function LoadDeviceByChartData() {
  const nowtime = (new Date()).getTime(); // 현재 시간의 Unix 타임
  const before30days = nowtime - (30 * 24 * 60 * 60 * 1000);  // 30일 전 (ms)

  // 입력값을 변수에 담고 문자열 형태로 변환
  const data = JSON.stringify({
    'operator': "getSearchDeivceByLineChartEx",
    'deviceid': props.setdevice.deviceid,
    'from': before30days,
    'to': nowtime
  });

  // content-type을 설정하고 데이터 송신
  const xhr = new XMLHttpRequest();
  xhr.open('POST', '/ajax/ajaxcall');
  xhr.setRequestHeader('Content-type', "application/json");
  xhr.send(data);

  // 데이터 수신이 완료되면 표시
  xhr.addEventListener('load', function () {
    var result = xhr.responseText;
    if (!result || result == '')
      return;

    const chartdata = JSON.parse(result);
    setChartDataList.value = chartdata;

    initDevice();
  });
}


function settingDevice() {
    ConfigShowModal.value = true;
}

function handleConfigUpdated() {
    // 센서 설정이 업데이트되었을 때 디바이스 설정을 다시 로드
    // reloadDeviceConfig()를 직접 호출하는 대신, 부모에게 알림
    reloadDeviceConfig();
}

// 디바이스 정보를 다시 로드
function handleDeviceUpdated() {
    // emit의 두 번째 파라미터로 deviceid 전달
    emit('client-device-updated', props.setdevice.deviceid)
}

function reloadDeviceConfig() {
    const data = JSON.stringify({
        'operator': "loadDeviceConfig",
        'deviceid': props.setdevice.deviceid
    });

    var xhr = new XMLHttpRequest();
    xhr.open('POST', '/ajax/ajaxcall');
    xhr.setRequestHeader('Content-type', "application/json");
    xhr.send(data);

    xhr.addEventListener('load', () => {
        let result = JSON.parse(xhr.responseText);
        if(result && result.err === 0) {
            emit('config-updated', {
                deviceid: props.setdevice.deviceid,
                config: result.data.config,
                alertconfig: result.data.alertconfig
            });
        }
    });
}

</script>


<style lang="">
    
</style>